// DialogProvider.tsx
import { FormControlLabel, Switch } from "@mui/material";
import Select, { components } from 'react-select';
import { Utils } from "helpers/utils";
import React, { createContext, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { APIClient } from "api/apiCore";
import * as url from "api/urls"
import { SettingsState } from "../src/redux/settings/types";
import { useRedux } from "hooks";
import { createSelector } from "reselect";
import { setOrgDdlValueForPerm } from "../src/redux/actions";
import Loader from "components/Loader";

type BasicDialogOptions = {
  title?: string;
  message?: React.ReactNode;
  okText?: string;
  cancelText?: string;
  showCancel?: boolean;
};

type DialogContextType = {
  show: (opts: BasicDialogOptions) => Promise<any>;
  confirm: (message: string, title?: string) => Promise<boolean>;
  showConfirmPassword: (opts?: { title?: string; apiUrl?: string }) => Promise<boolean>;
  showPermConfig: (opts?: { permName: string, entryId?: number | null, permKey: string, defaultAllow?: boolean }) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextType | null>(null);

export const useDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used inside DialogProvider");
  return ctx;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [opts, setOpts] = useState<BasicDialogOptions | null>(null);
  const [resolver, setResolver] = useState<((value: any) => void) | null>(null);
  const [special, setSpecial] = useState<null | { type: "confirmPassword"; apiUrl?: string; title?: string }>(null);

  const [resolverPerm, setResolverPerm] = useState<((value: any) => void) | null>(null);
  const [specialPerm, setSpecialPerm] = useState<null | { permName: string, entryId: number | null, permKey: string, defaultAllow: boolean }>(null);

  // Basic show (same as previous example)
  const show = (options: BasicDialogOptions) => {
    return new Promise<any>((resolve) => {
      setOpts(options);
      setResolver(() => resolve);
    });
  };

  const confirm = (message: string, title?: string) => {
    return show({ message, title, okText: "OK", cancelText: "Cancel", showCancel: true })
      .then((v) => v === "ok");
  };

  // New: showConfirmPassword
  // apiUrl: endpoint to call for verification, expects JSON { username, password } -> { success: boolean, ... }
  const showConfirmPassword = (args?: { title?: string; apiUrl?: string }) => {
    return new Promise<boolean>((resolve) => {
      setSpecial({ type: "confirmPassword", apiUrl: args?.apiUrl, title: args?.title });
      // we reuse resolver to capture result
      setResolver(() => (val: any) => resolve(Boolean(val)));
    });
  };

  const showPermConfig = (args?: { permName: string, entryId?: number | null, permKey: string, defaultAllow?: boolean }) => {
    return new Promise<boolean>((resolve) => {
      setSpecialPerm({ permName: args?.permName ?? "", entryId: args?.entryId || null, permKey: args?.permKey || "", defaultAllow: args?.defaultAllow ?? false });
      setResolverPerm(() => (val: any) => resolve(Boolean(val)));
    });
  }

  const close = (value: any) => {
    console.log('close:', { value, resolver, resolverPerm });
    if (resolver) resolver(value);
    if (resolverPerm) resolverPerm(value);

    setOpts(null);
    setSpecial(null);
    setResolver(null);
    setSpecialPerm(null);
    setResolverPerm(null);
  };

  const contextValue: DialogContextType = {
    show,
    confirm,
    showConfirmPassword,
    showPermConfig
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {/* basic modal */}
      {opts && <DialogPortal opts={opts} onClose={close} />}
      {/* special confirm-password modal */}
      {special && special.type === "confirmPassword" && (
        <ConfirmPasswordPortal title={special.title} apiUrl={special.apiUrl} onClose={close} />
      )}
      {specialPerm && (
        <PermConfigPortal permName={specialPerm.permName} entryId={specialPerm.entryId} permKey={specialPerm.permKey} defaultAllow={specialPerm.defaultAllow} onClose={close} />
      )}
    </DialogContext.Provider>
  );
};

/* ----------------- Basic Dialog Portal (simple) ----------------- */
function DialogPortal({ opts, onClose }: { opts: BasicDialogOptions; onClose: (v: any) => void }) {
  const { title, message, okText = "OK", cancelText = "Cancel", showCancel = false } = opts;
  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle}>
        {title && <h3 style={{ marginTop: 0 }}>{title}</h3>}
        <div style={{ margin: "1rem 0" }}>{message}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {showCancel && <button onClick={() => onClose("cancel")}>{cancelText}</button>}
          <button onClick={() => onClose("ok")}>{okText}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ------------- ConfirmPassword Portal (built-in form) ------------- */

function ConfirmPasswordPortal({ title, apiUrl, onClose }: { title?: string; apiUrl?: string; onClose: (v: any) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // default API path if not provided
  const endpoint = apiUrl ?? "/api/auth/confirm-password";

  const submit = async () => {
    onClose(true);
  };

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0 }}>{title ?? "Confirm password"}</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontSize: 13 }}>
            Username
            <input
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={inputStyle}
              placeholder="username"
            />
          </label>

          <label style={{ fontSize: 13 }}>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              placeholder="password"
            />
          </label>

          {error && <div style={{ color: "crimson", fontSize: 13 }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={() => onClose(false)} disabled={loading}>Cancel</button>
            <button onClick={submit} disabled={loading}>{loading ? "Checking..." : "Confirm"}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PermConfigPortal({ permName, entryId, permKey, defaultAllow, onClose }: { permName: string, entryId: number | null, permKey: string, defaultAllow: boolean, onClose: (v: any) => void }) {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.Settings,
    (state: any) => state.Profile2,
    (st: SettingsState, p2) => ({
      activeTool: st.activeTool,
      isAdmin: p2.isAdmin,
      orgDllValueForPerm: st.orgDllValueForPerm,
    })
  );
  const { activeTool, isAdmin, orgDllValueForPerm } = useAppSelector(storeData);

  const [loading, setLoading] = useState(false);
  const [ddlDivison, setDdlDivison] = useState<any[] | null>(null);
  const [ddlDepartment, setDdlDepartment] = useState<any[] | null>(null);
  const [ddlPosition, setDdlPosition] = useState<any[] | null>(null);
  const [ddlUser, setDdlUser] = useState<any[] | null>(null);
  const [orgDllValueForPermLoading, setOrgDllValueForPermLoading] = useState<boolean>(false);

  const [permAllow, setPermAllow] = useState<boolean>(defaultAllow);
  const [selDivision, setSelDivision] = useState<any[]>([]);
  const [selDepartment, setSelDepartment] = useState<any[]>([]);
  const [selPosition, setSelPosition] = useState<any[]>([]);
  const [selUser, setSelUser] = useState<any[]>([]);

  const [ddlDepartmentFilter, setDdlDepartmentFilter] = useState<any[]>([]);
  const [ddlPositionFilter, setDdlPositionFilter] = useState<any[]>([]);
  const [ddlUserFilter, setDdlUserFilter] = useState<any[]>([]);

  const onDllItemSelected = (mode: "div" | "dep" | "pos" | "user", values: any[]) => {
    if (mode === "div") {
      setSelDivision(values);
    } else if (mode === "dep") {
      setSelDepartment(values);
    } else if (mode === "pos") {
      setSelPosition(values);
    } else if (mode == "user") {
      setSelUser(values);
    }
  }

  useEffect(() => {
    const selDiv = selDivision.map(e => e.divisionName);
    const selDep = ddlDepartment?.filter(x => selDiv.length <= 0 || selDiv.includes(x.divisionName));
    setDdlDepartmentFilter(selDep ?? []);
  }, [ddlDepartment, selDivision]);

  useEffect(() => {
    const selDiv = selDivision.map(e => e.divisionName);
    const selDep = selDepartment.map(e => e.departmentName);
    const selPos = ddlPosition?.filter(x => {
      const divMatch = selDiv.length <= 0 || selDiv.includes(x.divisionName);
      const depMatch = selDep.length <= 0 || selDep.includes(x.departmentName);
      return divMatch && depMatch
    });
    setDdlPositionFilter(selPos ?? []);
  }, [ddlPosition, selDivision, selDepartment]);

  useEffect(() => {
    const selDiv = selDivision.map(e => e.divisionName);
    const selDep = selDepartment.map(e => e.departmentName);
    const selPos = selPosition.map(e => e.positionName);
    const selUser = ddlUser?.filter(x => {
      const divMatch = selDiv.length <= 0 || selDiv.includes(x.divisionName);
      const depMatch = selDep.length <= 0 || selDep.includes(x.departmentName);
      const posMatch = selPos.length <= 0 || selPos.includes(x.positionName);
      return divMatch && depMatch && posMatch;
    });
    setDdlUserFilter(selUser ?? []);
  }, [ddlUser, selDivision, selDepartment, selPosition]);

  useEffect(() => {
    try {
      // console.log('orgDllValueForPerm: ', { orgDllValueForPerm });
      if (!orgDllValueForPerm && !orgDllValueForPermLoading) {
        setOrgDllValueForPermLoading(true);

        const api = new APIClient();
        api.fnPostForm(url.LOAD_DDL_VALUE_FOR_PERM)
          .then(rs => {
            dispatch(setOrgDdlValueForPerm(rs));
          })
          .catch(er => {
            Utils.HandleException(er, true);
          });
      }
    } catch (e) {
      Utils.HandleException(e, true);
    }
  });

  useEffect(() => {
    if (orgDllValueForPerm) {
      const distinctDiv = Array.from(
        new Map(
          orgDllValueForPerm
            .filter((item: any) => item.divisionName) // Step 1: filter out empty name1
            .map((item: any) => {
              const updated = { ...item, label: item.divisionName, value: item.divisionName };
              return [item.divisionName, updated];
            })
        ).values() // Get only the unique values
      ).sort((a: any, b: any) => a.divisionName.localeCompare(b.divisionName));

      const distinctDep = Array.from(
        new Map(
          orgDllValueForPerm
            .filter((item: any) => item.departmentName) // Step 1: filter out empty name1
            .map((item: any) => {
              const updated = { ...item, label: item.departmentName, value: item.departmentName };
              return [item.departmentName, updated];
            })
        ).values() // Get only the unique values
      ).sort((a: any, b: any) => a.departmentName.localeCompare(b.departmentName));

      const distinctPos = Array.from(
        new Map(
          orgDllValueForPerm
            .filter((item: any) => item.positionName) // Step 1: filter out empty name1
            .map((item: any) => {
              const updated = { ...item, label: item.positionName, value: item.positionName };
              return [item.positionName, updated];
            })
        ).values() // Get only the unique values
      ).sort((a: any, b: any) => a.positionName.localeCompare(b.positionName));

      const distinctEmp = (orgDllValueForPerm ?? []).sort((a: any, b: any) => a.displayName2.localeCompare(b.displayName2)).map((item: any) => ({ ...item, label: item.displayName2, value: item.userId }))
      setDdlDivison(distinctDiv);
      setDdlDepartment(distinctDep);
      setDdlPosition(distinctPos);
      setDdlUser(distinctEmp);
    }
  }, [orgDllValueForPerm]);

  useEffect(() => {
    if (entryId) {
      setLoading(true);
      const api = new APIClient();
      api.fnPostForm(url.LOAD_PERM_CONFIG2, { entryId: entryId })
        .then(rs => {
          console.log('LOAD_PERM_CONFIG2:', rs);
          setPermAllow(rs.allow);
          setSelDivision((rs.divisions?.split('|') ?? []).map((x: any) => ({ divisionName: x })));
          setSelDepartment((rs.departments?.split('|') ?? []).map((x: any) => ({ departmentName: x })));
          setSelPosition((rs.positions?.split('|') ?? []).map((x: any) => ({ positionName: x })));
          setSelUser((rs.userIds?.split('|') ?? []).map((x: any) => ({ userId: Utils.ToNumber(x, -1) ?? -1 })));
          setLoading(false);
        })
        .catch(er => {
          Utils.HandleException(er, true);
          setLoading(false);
        });
    }
  }, [entryId]);

  const onSave = () => {
    try {
      setLoading(true);
      const api = new APIClient();
      const parm = {
        entryId: entryId,
        permName: permKey,
        allow: permAllow,
        divisions: selDivision.map(x => x.divisionName).join('|'),
        departments: selDepartment.map(x => x.departmentName).join('|'),
        positions: selPosition.map(x => x.positionName).join('|'),
        userIds: selUser.map(x => x.userId).join('|'),
      }
      console.log(parm);
      api.fnPostForm(url.SAVE_PERM_CONFIG2, parm)
        .then(rs => {
          // console.log('SAVE_PERM_CONFIG2:', rs);
          onClose(true);
          setLoading(false);
        })
        .catch(er => {
          Utils.HandleException(er, true);
          setLoading(false);
        })
    } catch (e) {
      Utils.HandleException(e, true);
      setLoading(false);
    }
  }

  const onDel = () => {
    setLoading(true);
    new APIClient().fnPostForm(url.DEL_PERM_CONFIG2, { entryId: entryId })
      .then(rs => {
        setLoading(false);
        onClose(true);
      })
      .catch(er => {
        Utils.HandleException(er, true);
        setLoading(false);
      });

  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle}>
        {loading && <Loader />}
        <h3 style={{ marginTop: 0, borderBottom: "dashed 1px var(--m2-line-divider-color)" }}>กำหนดสิทธิ์ - <span className="text-danger">{permName}</span></h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <FormControlLabel
                  control={<Switch checked={permAllow} onChange={(e) => setPermAllow(e.target.checked)} />}
                  label={permAllow ? (<span className="text-success">อนุญาต</span>) : (<span className="text-danger">ไม่อนุญาติ</span>)} />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="หน่วยงาน"
                  options={ddlDivison as any[]}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("div", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  //value={item.division ? ddlDivison?.find(t => t.value === item.division) : null}
                  value={ddlDivison?.filter(x1 => selDivision.map(x => x.divisionName).includes(x1.divisionName))}
                  isClearable={true}
                ></Select>
              </div>
            </div>
            <div className="row">

              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="แผนก"
                  options={ddlDepartmentFilter}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("dep", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  value={ddlDepartment?.filter(x1 => selDepartment.map(x => x.departmentName).includes(x1.departmentName))}
                  isClearable={true}
                ></Select>
              </div>
            </div>
            <div className="row">

              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="ตำแหน่ง"
                  options={ddlPositionFilter}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("pos", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  value={ddlPosition?.filter(x1 => selPosition.map(x => x.positionName).includes(x1.positionName))}
                  isClearable={true}
                ></Select>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="พนักงาน"
                  options={ddlUserFilter}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("user", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  value={ddlUser?.filter(x1 => selUser.map(x => x.userId).includes(x1.userId))}
                  isClearable={true}
                ></Select>
              </div>
              {/* <div className="col">
                <input type="text" className="form-control" placeholder="พนักงาน" />
              </div>                                                   */}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, borderTop: "dashed 1px var(--m2-line-divider-color)" }}>
            <div>
              <button className="btn btn-danger" onClick={onDel} disabled={loading}>ลบ</button>
            </div>
            <div>
              <button className="btn btn-warning" onClick={() => onClose(false)} disabled={loading}>ยกเลิก</button>
              <button className="btn btn-primary" onClick={onSave} disabled={loading}>บันทึก</button>
            </div>
            {/* <button onClick={submit} disabled={loading}>{loading ? "Checking..." : "Confirm"}</button> */}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ---------------- Styles (minimal) ---------------- */
const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: "var(--m2-wp-conver-content-bg)",
  border: "solid 1px var(--m2-wp-conver-content-border)",
  padding: 16,
  borderRadius: 8,
  width: "100%",
  maxWidth: 768,
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  marginTop: 6,
  borderRadius: 6,
  border: "1px solid #ccc",
};
